// tippi/src/pages/admin/broadcasts/AdminBroadcasts.js
import React, { useState, useEffect } from 'react';
import { collection, query, where, orderBy, onSnapshot, getDocs } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../../../firebase/config';
import { COLORS, SHADOWS } from '../../../styles/tokens';
import { useIsMobile } from '../../../hooks/useIsMobile';

const TARGET_OPTS = [
  { value: 'all',      label: 'Alle' },
  { value: 'driver',   label: 'Kun sjåfører' },
  { value: 'orderer',  label: 'Kun bestillere' },
];
const MODE_OPTS = [
  { value: 'inbox', label: 'Innboks' },
  { value: 'modal', label: 'Pop-up modal' },
];

// Kjennetegner uredigert "Fyll inn forslag"-tekst (• <7-tegns git-hash> <melding>).
// Brukes til å varsle admin i forhåndsvisningen dersom rå commit-tekst er på vei ut til brukere.
const RAW_COMMIT_PATTERN = /^•\s*[0-9a-f]{7}\s/m;

export default function AdminBroadcasts() {
  const [title,       setTitle]       = useState('');
  const [body,        setBody]        = useState('');
  const [target,      setTarget]      = useState('all');
  const [displayMode, setDisplayMode] = useState('inbox');
  const [sending,     setSending]     = useState(false);
  const [error,       setError]       = useState('');
  const [success,     setSuccess]     = useState('');
  const [broadcasts,  setBroadcasts]  = useState([]);
  const [pendingLogs, setPendingLogs] = useState([]);
  const [usedLogFill, setUsedLogFill] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    const q = query(collection(db, 'broadcasts'), orderBy('createdAt', 'desc'));
    return onSnapshot(q, snap => setBroadcasts(snap.docs.map(d => ({ id: d.id, ...d.data() }))), () => {});
  }, []);

  useEffect(() => {
    getDocs(query(collection(db, 'systemUpdateLog'), where('broadcasted', '==', false)))
      .then(snap => setPendingLogs(snap.docs.map(d => ({ id: d.id, ...d.data() }))))
      .catch(() => {});
  }, []);

  function fillFromLogs() {
    setTitle('Systemoppdatering');
    const lines = pendingLogs.flatMap(l => l.commits ?? []).map(c => `• ${c}`).join('\n');
    setBody(lines || '(ingen endringer registrert)');
    setDisplayMode('modal');
    setUsedLogFill(true);
  }

  function handleReview() {
    if (!title.trim() || !body.trim()) { setError('Fyll inn tittel og melding'); return; }
    setError('');
    setPreviewOpen(true);
  }

  async function handleConfirmSend() {
    setSending(true);
    setError('');
    setSuccess('');
    try {
      const fns = getFunctions(undefined, 'europe-west1');
      await httpsCallable(fns, 'sendBroadcast')({ title: title.trim(), body: body.trim(), target, displayMode });
      if (usedLogFill && pendingLogs.length > 0) {
        await httpsCallable(fns, 'markSystemUpdateBroadcasted')({ logIds: pendingLogs.map(l => l.id) });
        setPendingLogs([]);
        setUsedLogFill(false);
      }
      setTitle('');
      setBody('');
      setSuccess('Melding sendt!');
      setPreviewOpen(false);
    } catch (err) {
      setError(err.message ?? 'Sending feilet. Prøv igjen.');
    } finally {
      setSending(false);
    }
  }

  return (
    <div style={{ ...styles.page, ...(isMobile ? { padding: 16 } : {}) }}>
      {pendingLogs.length > 0 && (
        <div style={styles.updateBanner}>
          <span>📦 {pendingLogs.length} {pendingLogs.length === 1 ? 'ny endring' : 'nye endringer'} siden siste varsling</span>
          <button style={styles.fillBtn} onClick={fillFromLogs}>Fyll inn utkast (rediger før sending!)</button>
        </div>
      )}

      <div style={styles.composer}>
        <h2 style={styles.heading}>Send melding</h2>

        {error   && <div style={styles.errorBox}>{error}</div>}
        {success && <div style={styles.successBox}>{success}</div>}

        <label style={styles.label}>Tittel</label>
        <input
          style={styles.input}
          value={title}
          onChange={e => setTitle(e.target.value)}
          placeholder="F.eks. Systemoppdatering"
        />

        <label style={styles.label}>Melding</label>
        <textarea
          style={{ ...styles.input, height: 120, resize: 'vertical' }}
          value={body}
          onChange={e => setBody(e.target.value)}
          placeholder="Skriv meldingen her…"
        />

        <label style={styles.label}>Målgruppe</label>
        <div style={styles.pillRow}>
          {TARGET_OPTS.map(o => (
            <button key={o.value} type="button" style={{ ...styles.pill, ...(target === o.value ? styles.pillActive : {}) }} onClick={() => setTarget(o.value)}>
              {o.label}
            </button>
          ))}
        </div>

        <label style={styles.label}>Visningstype</label>
        <div style={styles.pillRow}>
          {MODE_OPTS.map(o => (
            <button key={o.value} type="button" style={{ ...styles.pill, ...(displayMode === o.value ? styles.pillActive : {}) }} onClick={() => setDisplayMode(o.value)}>
              {o.label}
            </button>
          ))}
        </div>

        <button style={styles.sendBtn} onClick={handleReview} disabled={sending}>
          👁 Se forhåndsvisning
        </button>
      </div>

      {previewOpen && (
        <div style={styles.previewOverlay}>
          <div style={styles.previewPanel}>
            <h3 style={styles.previewHeading}>
              Slik vil {TARGET_OPTS.find(o => o.value === target)?.label.toLowerCase()} se meldingen
            </h3>
            <div style={styles.previewMeta}>
              Visningstype: {MODE_OPTS.find(o => o.value === displayMode)?.label}
            </div>

            {RAW_COMMIT_PATTERN.test(body) && (
              <div style={styles.warnBox}>
                ⚠️ Dette ser ut som uredigert commit-tekst fra «Fyll inn utkast». Skriv om til
                vanlig, brukervennlig språk før du sender — brukere skal ikke se git-historikk.
              </div>
            )}

            {displayMode === 'modal' ? (
              <div style={styles.previewModalMock}>
                <div style={styles.previewTag}>Melding fra Tippi</div>
                <div style={styles.previewTitle}>{title}</div>
                <div style={styles.previewBody}>{body}</div>
                <div style={styles.previewFakeBtn}>Forstått</div>
              </div>
            ) : (
              <div style={styles.previewInboxMock}>
                <div style={styles.previewTitle}>{title}</div>
                <div style={styles.previewBody}>{body}</div>
              </div>
            )}

            {error && <div style={styles.errorBox}>{error}</div>}

            <div style={styles.previewActions}>
              <button style={styles.editBtn} onClick={() => setPreviewOpen(false)} disabled={sending}>
                ← Rediger
              </button>
              <button style={styles.confirmSendBtn} onClick={handleConfirmSend} disabled={sending}>
                {sending ? 'Sender…' : `✓ Godkjenn og send til ${TARGET_OPTS.find(o => o.value === target)?.label.toLowerCase()}`}
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={styles.historySection}>
        <h3 style={styles.historyHeading}>Sendt historikk</h3>
        {broadcasts.length === 0 && <p style={styles.empty}>Ingen meldinger sendt ennå.</p>}
        {broadcasts.map(bc => (
          <div key={bc.id} style={styles.historyRow}>
            <div style={{ flex: 1 }}>
              <div style={styles.historyTitle}>{bc.title}</div>
              <div style={styles.historyMeta}>
                {TARGET_OPTS.find(o => o.value === bc.target)?.label} ·{' '}
                {MODE_OPTS.find(o => o.value === bc.displayMode)?.label} ·{' '}
                {bc.createdAt?.toDate?.().toLocaleDateString('nb-NO', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) ?? ''}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const styles = {
  page:            { padding: '24px 28px', overflowY: 'auto', height: '100%' },
  updateBanner:    { display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, background: COLORS.warningBg, border: `1.5px solid ${COLORS.warningBorder}`, borderRadius: 8, padding: '12px 16px', marginBottom: 20 },
  fillBtn:         { padding: '6px 14px', borderRadius: 6, border: 'none', background: COLORS.primary, color: COLORS.onPrimary, fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  composer:        { background: COLORS.white, borderRadius: 12, padding: '24px', marginBottom: 28, boxShadow: SHADOWS.sm, display: 'flex', flexDirection: 'column', gap: 10 },
  heading:         { fontSize: 18, fontWeight: 700, color: COLORS.textDark, margin: '0 0 8px' },
  label:           { fontSize: 13, fontWeight: 600, color: COLORS.textMid },
  input:           { padding: '10px 12px', borderRadius: 8, border: `1.5px solid ${COLORS.border}`, fontSize: 14, width: '100%', boxSizing: 'border-box', background: COLORS.white, color: COLORS.textDark },
  pillRow:         { display: 'flex', gap: 8, flexWrap: 'wrap' },
  pill:            { padding: '7px 16px', borderRadius: 20, border: `1.5px solid ${COLORS.border}`, background: COLORS.white, color: COLORS.textDark, fontSize: 13, cursor: 'pointer', fontWeight: 500 },
  pillActive:      { background: COLORS.primary, color: COLORS.onPrimary, borderColor: COLORS.primary, fontWeight: 700 },
  sendBtn:         { padding: '13px', borderRadius: 8, border: 'none', background: COLORS.primary, color: COLORS.onPrimary, fontSize: 15, fontWeight: 700, cursor: 'pointer', marginTop: 4 },
  errorBox:        { background: COLORS.dangerBg, color: COLORS.dangerText, padding: '10px 14px', borderRadius: 8, fontSize: 14 },
  successBox:      { background: COLORS.successBg, color: COLORS.successText, padding: '10px 14px', borderRadius: 8, fontSize: 14 },
  historySection:  { background: COLORS.white, borderRadius: 12, padding: '20px 24px', boxShadow: SHADOWS.sm },
  historyHeading:  { fontSize: 15, fontWeight: 700, color: COLORS.textDark, margin: '0 0 16px' },
  historyRow:      { display: 'flex', padding: '12px 0', borderBottom: `1px solid ${COLORS.border}` },
  historyTitle:    { fontSize: 14, fontWeight: 600, color: COLORS.textDark, marginBottom: 4 },
  historyMeta:     { fontSize: 12, color: COLORS.textLight },
  empty:           { color: COLORS.textLight, fontSize: 14 },

  // ── Forhåndsvisning / godkjenning ───────────────────────────────────────
  previewOverlay:    { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 },
  previewPanel:      { background: COLORS.white, borderRadius: 14, padding: '28px 26px', maxWidth: 460, width: '100%', maxHeight: '86vh', overflowY: 'auto', boxShadow: SHADOWS.lg },
  previewHeading:    { fontSize: 16, fontWeight: 700, color: COLORS.textDark, margin: '0 0 4px' },
  previewMeta:       { fontSize: 12, color: COLORS.textLight, marginBottom: 16 },
  warnBox:           { background: COLORS.warningBg, border: `1.5px solid ${COLORS.warningBorder}`, borderRadius: 8, padding: '10px 12px', fontSize: 13, color: COLORS.warningText, marginBottom: 16, lineHeight: 1.5 },
  previewModalMock:  { background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 12, padding: '20px 18px', marginBottom: 16 },
  previewTag:        { display: 'inline-block', fontSize: 10, fontWeight: 700, color: COLORS.textDark, background: COLORS.surfaceHover, borderRadius: 20, padding: '3px 10px', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 },
  previewFakeBtn:    { width: '100%', padding: '11px', border: 'none', background: COLORS.primary, color: COLORS.onPrimary, borderRadius: 8, fontSize: 14, fontWeight: 600, textAlign: 'center', marginTop: 14 },
  previewInboxMock:  { background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 12, padding: '16px 18px', marginBottom: 16 },
  previewTitle:      { fontSize: 17, fontWeight: 700, color: COLORS.textDark, margin: '0 0 8px' },
  previewBody:       { fontSize: 14, color: COLORS.textMid, lineHeight: 1.6, whiteSpace: 'pre-wrap', margin: 0 },
  previewActions:    { display: 'flex', gap: 10, marginTop: 4 },
  editBtn:           { flex: '0 0 auto', padding: '11px 16px', borderRadius: 8, border: `1.5px solid ${COLORS.border}`, background: COLORS.white, color: COLORS.textMid, fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  confirmSendBtn:    { flex: 1, padding: '11px 16px', borderRadius: 8, border: 'none', background: COLORS.primary, color: COLORS.onPrimary, fontSize: 14, fontWeight: 700, cursor: 'pointer' },
};
